import { type TUseRequestUpdaterProps, useRequest } from '@features/request/useRequest';
import { useResetStoreData } from '@features/request/useResetStoreData';
import type { CatalogRatioDataForList, CatalogRatiosQuery } from '@gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { catalogRatiosGql } from '../../Common/CatalogRatios';

type TUseBaseCostRatioGroupsDataProps = {
    ratioGroups: (string | number)[];
};

const requestId = 'baseCostRatiosByGroupsData';

export const useBaseCostRatiosByGroupsData = ({ ratioGroups }: TUseBaseCostRatioGroupsDataProps) => {
    const [data, setData] = useState<CatalogRatioDataForList[]>([]);

    const dispatch = useDispatch();

    const [ratiosReset] = useResetStoreData({ requestId });

    const reset = useCallback(() => {
        ratiosReset(undefined);
    }, [ratiosReset]);

    const variables = useMemo(
        () => ({
            where: {
                catalogRatioGroupId: { in: ratioGroups },
            },
        }),
        [ratioGroups],
    );

    const onSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<CatalogRatiosQuery>) => {
            setData(depsVars?.payload?.catalogRatios?.items || []);
            reset();
        },
        [reset],
    );

    const effect = useMemo(() => !!ratioGroups?.length, [ratioGroups?.length]);

    const [, , , ratiosMakeRequest] = useRequest({
        requestId,
        query: catalogRatiosGql,
        variables,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const request = useCallback(() => {
        if (effect) {
            dispatch(ratiosMakeRequest(variables));
        }
    }, [effect, dispatch, ratiosMakeRequest, variables]);

    useEffect(() => {
        setData([]);
        reset();
        request();
    }, [request, reset]);

    return useMemo(() => ({ data }), [data]);
};
